import React, { useState } from 'react';
import useAuth from '../hooks/useAuth.js';
import AuthAppBarActions from './AuthAppBarActions.js';
import TablePagination from '@mui/material/TablePagination';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Typography, IconButton, Tooltip, Select, MenuItem, InputLabel, FormControl, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { CheckCircle, HourglassEmpty, Cancel, LocalShipping, Assignment, Info, Print, Layers, Done, Settings, Build, Delete as DeleteIcon } from '@mui/icons-material';
import { GOLD, ERROR_RED } from '../constants/colors.js';
import { useEffect, useRef } from 'react';
import { db } from '../firebase.js';
import { collection, getDocs, doc, updateDoc, arrayUnion, deleteDoc, onSnapshot } from 'firebase/firestore';

import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';


// Stage icon and color mapping
const STAGE_META = {
  'جديد': { icon: <HourglassEmpty />, color: '#FFA726' },
  'طلب الورق': { icon: <Layers />, color: '#90caf9' },
  'الطباعة': { icon: <Print />, color: '#FFD600' },
  'سولوفان': { icon: <Settings />, color: '#A7FFEB' },
  'تكسير': { icon: <Settings />, color: '#F06292' },
  'تجهيز بعد الطباعة': { icon: <Build />, color: '#FF4081' },
  'تقفيل': { icon: <Done />, color: '#00C853' },
  'جاهز للتسليم': { icon: <LocalShipping />, color: '#00B8D4' },
  'تم التسليم': { icon: <CheckCircle />, color: '#FFD700' },
};

const POST_PRESS_META = {
  'يو في': { icon: <Settings />, color: '#00E676' },
  'بصمه': { icon: <Settings />, color: '#FFD700' },
  'امبوس': { icon: <Settings />, color: '#F8BBD0' },
};

// Helper to compute Remaining
// Helper to compute Remaining
function calculateRemaining(row) {
  // Use lower-case keys to match Firestore/state
  const order = Number(row['order']) || 0;
  const finished = Number(row['finished']) || 0;
  return order - finished;
}



// No more mockOrders, data is loaded from Firestore

const statusIcons = {
  Success: <CheckCircle sx={{ color: '#00C853' }} />,
  Pending: <HourglassEmpty sx={{ color: GOLD }} />,
  Cancelled: <Cancel sx={{ color: ERROR_RED }} />,
};

const fulfillmentColors = {
  Fulfilled: 'success',
  Unfulfilled: 'warning',
  Cancelled: 'error',
};


export default function OrderTable() {
  const { user, role } = useAuth();
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [editingRowId, setEditingRowId] = useState(null); // id of row being edited
  const [historyDialog, setHistoryDialog] = useState({ open: false, history: [], title: '' });
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'no', direction: 'asc' });
  const originalRows = useRef([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, order: null });

  // State for flashing updated rows
  const [flashingRowIds, setFlashingRowIds] = useState([]);


  // State for adding new record
  const [addingNew, setAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({
    no: '',
    client: '',
    product: '',
    image: '',
    order: '',
    finished: '',
    delivered: '',
    stage: '',
    subStage: '',
    expectedFinish: '',
    orderExpectedFinish: '',
    notes: '',
    history: []
  });

  // State to track image loading for add/edit
  const [imageLoading, setImageLoading] = useState(false);

  // Compress image utility
  function compressImage(file, maxWidth = 800, quality = 0.7) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const reader = new FileReader();
      reader.onload = e => {
        img.onload = () => {
          const scale = Math.min(maxWidth / img.width, 1);
          const width = img.width * scale;
          const height = img.height * scale;
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // حذف الطلب
  const handleDeleteOrder = async () => {
    if (!deleteDialog.order) return;
    try {
      await deleteDoc(doc(db, 'orders', deleteDialog.order.id));
      setRows(prev => prev.filter(row => row.id !== deleteDialog.order.id));
      setDeleteDialog({ open: false, order: null });
    } catch (err) {
      alert('حدث خطأ أثناء حذف الطلب');
    }
  };


  // Sorting function
  function getSortedRows(rows) {
    const sorted = [...rows];
    const { key, direction } = sortConfig;
    sorted.sort((a, b) => {
      if (key === 'expectedFinish') {
        // Sort by date, ignoring empty/invalid dates (put them at the end)
        const isValidA = !!a.expectedFinish && !isNaN(new Date(a.expectedFinish));
        const isValidB = !!b.expectedFinish && !isNaN(new Date(b.expectedFinish));
        if (!isValidA && !isValidB) return 0;
        if (!isValidA) return 1;
        if (!isValidB) return -1;
        const dateA = new Date(a.expectedFinish);
        const dateB = new Date(b.expectedFinish);
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (key === 'no') {
        // Sort by order number (numeric)
        return direction === 'asc' ? (a.no - b.no) : (b.no - a.no);
      } else {
        // fallback
        return 0;
      }
    });
    return sorted;
  }

  function handleSort(key) {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      } else {
        return { key, direction: 'asc' };
      }
    });
  }

  useEffect(() => {
    async function fetchOrders() {
      setLoading(true);
     const unsubscribe = onSnapshot(collection(db, 'orders'), (snapshot) => {
      const newOrders = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      // Detect changed rows
      const changedIds = [];
      newOrders.forEach(order => {
        const prev = originalRows.current.find(r => r.id === order.id);
        if (!prev) {
          // New row
          changedIds.push(order.id);
        } else {
          // Only compare meaningful fields
          if (
            prev.client !== order.client ||
            prev.product !== order.product ||
            prev.order !== order.order ||
            prev.finished !== order.finished ||
            prev.delivered !== order.delivered ||
            prev.stage !== order.stage ||
            prev.subStage !== order.subStage ||
            prev.expectedFinish !== order.expectedFinish ||
            prev.orderExpectedFinish !== order.orderExpectedFinish ||
            prev.notes !== order.notes
          ) {
            changedIds.push(order.id);
          }
        }
      });
      setRows(newOrders);
      originalRows.current = newOrders.map(order => ({ ...order }));
      if (changedIds.length > 0) {
        // Flash twice: on, off, on, off
        setFlashingRowIds(changedIds);
        setTimeout(() => setFlashingRowIds([]), 200);
        setTimeout(() => setFlashingRowIds(changedIds), 400);
        setTimeout(() => setFlashingRowIds([]), 600);
      }
    });
    setLoading(false);
    }
    fetchOrders();
  }, []);


  const handleStageChange = (id, value) => {
    setRows(prev => prev.map(row => row.id === id ? { ...row, stage: value, subStage: value === 'Post-Press' ? row.subStage : 'N/A' } : row));
  };
  const handleSubStageChange = (id, value) => {
    setRows(prev => prev.map(row => row.id === id ? { ...row, subStage: value } : row));
  };

  const handleFieldChange = (id, field, value) => {
    setRows(prev => prev.map(row => {
      if (row.id !== id) return row;
      if (["order", "finished", "delivered"].includes(field)) {
        return { ...row, [field]: value === '' ? '' : Number(value) };
      }
      return { ...row, [field]: value };
    }));
  };

  // Start editing a row
  const handleEditRow = id => {
    setEditingRowId(id);
  };

  // Cancel editing a row
  const handleCancelEditRow = () => {
    setRows(prev => prev.map(row =>
      row.id === editingRowId ? { ...originalRows.current.find(orig => orig.id === editingRowId) } : row
    ));
    setEditingRowId(null);
  };

  // Save changes for a single row
  const handleSaveRow = async id => {
    const updated = rows.find(row => row.id === id);
    const original = originalRows.current.find(row => row.id === id);
    if (!updated || !updated.id) {
      alert('Order is missing an ID. Cannot save.');
      return;
    }
    let changes = [];
    ["stage", "subStage", "expectedFinish", "orderExpectedFinish", "notes", "finished", "delivered", "order", "image"].forEach(field => {
      if (original[field] !== updated[field]) {
        const change = {
          timestamp: new Date().toISOString(),
          field,
          oldValue: original[field],
          newValue: updated[field],
          user: 'currentUser',
        };
        if (field === 'notes') {
          change.note = updated[field];
        }
        changes.push(change);
      }
    });
    if (changes.length) {
      // Filter out any undefined or invalid change objects
      let validChanges = changes.filter(c => c && c.field && c.timestamp && c.hasOwnProperty('oldValue') && c.hasOwnProperty('newValue'));
      // Remove any undefined properties from each change object
      validChanges = validChanges.map(change => {
        const sanitized = {};
        Object.keys(change).forEach(key => {
          if (change[key] !== undefined) sanitized[key] = change[key];
        });
        return sanitized;
      });
      if (!validChanges.length) {
        alert('No valid changes to save.');
        setEditingRowId(null);
        return;
      }
      const docRef = doc(db, 'orders', updated.id);
      const lastNote = validChanges.filter(c => c.field === 'notes').pop();
      await updateDoc(docRef, {
        ...(["notes"].some(f => validChanges.find(c => c.field === f)) ? { notes: lastNote?.newValue || updated.notes } : {}),
        stage: updated.stage,
        subStage: updated.subStage,
        expectedFinish: updated.expectedFinish,
        orderExpectedFinish: updated.orderExpectedFinish,
        finished: updated.finished,
        delivered: updated.delivered,
        order: updated.order,
        image: updated.image,
        history: arrayUnion(...validChanges)
      });
      setEditingRowId(null);
    } else {
      alert('No changes were saved.');
      setEditingRowId(null);
    }
  };


  const handleViewHistory = (row) => {
    setHistoryDialog({
      open: true,
      history: (row.history || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
      title: `History for #${row.no} - ${row.product}`
    });
  };

  const handleCloseHistory = () => setHistoryDialog({ open: false, history: [], title: '' });


  return (
    <Box sx={{ mt: 4 }}>
      {/* زر إضافة سجل جديد للمشرفين والإداريين */}
      {(role === 'admin' || role != 'viewer') && !addingNew && (
        <Button variant="contained" color="primary" sx={{ mb: 2, fontWeight: 700 }} onClick={() => setAddingNew(true)}>
          إضافة سجل جديد
        </Button>
      )}
      {/* صف إضافة سجل جديد */}
      {(role === 'admin' || role === 'supervisor') && addingNew && (
        <Paper elevation={2} sx={{ mb: 2, p: 2, background: 'rgba(40,40,40,0.97)', border: `2px solid ${GOLD}` }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField label="رقم الطلب" value={newRow.no} onChange={e => setNewRow({ ...newRow, no: e.target.value })} type="number" sx={{ width: 100 }} />
            <TextField label="العميل *" value={newRow.client} onChange={e => setNewRow({ ...newRow, client: e.target.value })} required error={!newRow.client} helperText={!newRow.client ? 'اسم العميل مطلوب' : ''} sx={{ width: 180 }} />
            <TextField label="المنتج" value={newRow.product} onChange={e => setNewRow({ ...newRow, product: e.target.value })} sx={{ width: 140 }} />
            <TextField label="الكمية" value={newRow.order} onChange={e => setNewRow({ ...newRow, order: e.target.value })} type="number" sx={{ width: 90 }} />
            <TextField label="تم الإنتاج" value={newRow.finished} onChange={e => setNewRow({ ...newRow, finished: e.target.value })} type="number" sx={{ width: 90 }} />
            <TextField label="تم التسليم" value={newRow.delivered} onChange={e => setNewRow({ ...newRow, delivered: e.target.value })} type="number" sx={{ width: 90 }} />
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>المرحلة</InputLabel>
              <Select value={newRow.stage} label="المرحلة" onChange={e => setNewRow({ ...newRow, stage: e.target.value })}>
                {Object.keys(STAGE_META).map(stage => (
                  <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>المرحلة الفرعية</InputLabel>
              <Select value={newRow.subStage} label="المرحلة الفرعية" onChange={e => setNewRow({ ...newRow, subStage: e.target.value })}>
                {(newRow.stage === 'تجهيز بعد الطباعة' ? Object.keys(POST_PRESS_META) : ['غير متوفر']).map(sub => (
                  <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="نهاية المرحلة المتوقعة" type="date" value={newRow.expectedFinish} onChange={e => setNewRow({ ...newRow, expectedFinish: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <TextField label="تاريخ الانتهاء المتوقع" type="date" value={newRow.orderExpectedFinish} onChange={e => setNewRow({ ...newRow, orderExpectedFinish: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <TextField label="ملاحظات" value={newRow.notes} onChange={e => setNewRow({ ...newRow, notes: e.target.value })} sx={{ width: 180 }} multiline maxRows={2} />
            <Button variant="contained" color="success" sx={{ fontWeight: 700 }} disabled={imageLoading} onClick={async () => {
              if (!newRow.client || imageLoading) return;
              try {
                const docRef = await import('../firebase.js').then(({ db }) => import('firebase/firestore').then(fb => fb.addDoc(fb.collection(db, 'orders'), {
                  ...newRow,
                  order: Number(newRow.order) || 0,
                  finished: Number(newRow.finished) || 0,
                  delivered: Number(newRow.delivered) || 0,
                  remaining: (Number(newRow.order) || 0) - (Number(newRow.finished) || 0),
                  stage: newRow.stage || '',
                  subStage: newRow.subStage || '',
                  expectedFinish: newRow.expectedFinish || '',
                  orderExpectedFinish: newRow.orderExpectedFinish || '',
                  notes: newRow.notes || '',
                  image: newRow.image || '',
                  history: []
                })));
                setAddingNew(false);
                setNewRow({ no: '', client: '', product: '', image: '', order: '', finished: '', delivered: '', stage: '', subStage: '', expectedFinish: '', orderExpectedFinish: '', notes: '', history: [] });
                // تحديث الجدول
                // No need to manually fetch orders; real-time listener will auto-update

              } catch (err) {
                alert('خطأ أثناء إضافة السجل');
              }
            }}>حفظ</Button>
            <Button variant="outlined" color="error" sx={{ fontWeight: 700 }} onClick={() => { setAddingNew(false); setNewRow({ no: '', client: '', product: '', image: '', order: '', finished: '', delivered: '', stage: '', subStage: '', expectedFinish: '', orderExpectedFinish: '', notes: '', history: [] }); }}>إلغاء</Button>
          </Box>
        </Paper>
      )}

      <Dialog open={!!enlargedImage} onClose={() => setEnlargedImage(null)} maxWidth="md" fullWidth dir='rtl'>
        <DialogTitle>
          عرض الصورة
          <Button onClick={() => setEnlargedImage(null)} sx={{ float: 'left' }} color="error">إغلاق</Button>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          {enlargedImage && (
            <img src={enlargedImage} alt="Enlarged Order" style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 25, boxShadow: '0 2px 24px #2228', backgroundColor: '#fff' }} />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={historyDialog.open} onClose={handleCloseHistory} maxWidth="sm" fullWidth />
      {/* Dialog حذف الطلب */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, order: null })}>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>تأكيد حذف الطلب</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>هل أنت متأكد أنك تريد حذف هذا الطلب؟ لا يمكن التراجع عن هذه العملية.</Typography>
          <Typography variant="body2" sx={{ color: GOLD }}>
            رقم الطلب: {deleteDialog.order?.no} — المنتج: {deleteDialog.order?.product}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, order: null })} color="primary">إلغاء</Button>
          <Button onClick={handleDeleteOrder} color="error" variant="contained">حذف</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={historyDialog.open} onClose={handleCloseHistory} maxWidth="sm" fullWidth>
        <DialogTitle>{historyDialog.title}</DialogTitle>
        <DialogContent>
          <Timeline>
            {historyDialog.history.length === 0 && <Typography>لا يوجد تاريخ.</Typography>}
            {historyDialog.history.map((h, idx) => (
              <TimelineItem key={idx}>
                <TimelineSeparator>
                  <TimelineDot color={h.field === 'notes' ? 'primary' : 'grey'} />
                  {idx < historyDialog.history.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="body2">{new Date(h.timestamp).toLocaleString()}</Typography>
                  <Typography variant="subtitle2">{h.field}</Typography>
                  <Typography variant="body1">
                    {h.field === 'notes' ? `Note: ${h.newValue}` : `${h.oldValue} → ${h.newValue}`}
                  </Typography>
                  {h.user && <Typography variant="caption">بواسطة: {h.user}</Typography>}
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </DialogContent>
      </Dialog>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          background: 'rgba(32,32,32,0.92)',
          boxShadow: 'none',
          backdropFilter: 'blur(8px)',
          border: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, m: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Assignment sx={{ color: GOLD, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: GOLD, letterSpacing: 1 }}>
              انتاج شركة فرزل الصناعية
            </Typography>
          </Box>
          <AuthAppBarActions />
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: GOLD, fontWeight: 700, cursor: 'pointer' }} onClick={() => handleSort('no')}>
  # {sortConfig.key === 'no' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''} الرقم
</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>العميل</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>المنتج</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>صورة المنتج</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>الكمية</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>تم الإنتاج</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>تم التسليم</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>المتبقي</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>المرحلة</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>المرحلة الفرعية</TableCell>
                <TableCell
                  sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => handleSort('expectedFinish')}
                >
                  نهاية المرحلة المتوقعة {sortConfig.key === 'expectedFinish' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>تاريخ الانتهاء المتوقع</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>ملاحظات</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>السجل</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSortedRows(rows)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(order => (
                  <TableRow
                    key={order.id}
                    sx={{
                      transition: 'background 0.3s',
                      background: flashingRowIds.includes(order.id)
                        ? (theme) => '#FFD70050'
                        : 'inherit',
                        ":hover": {
                          background: (theme) => '#FFD70010',
                        },
                    }}
                  >
                    <TableCell sx={{ color: GOLD, fontFamily: 'monospace', fontWeight: 700 }}>{order.no}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{order.client}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{order.product}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {editingRowId === order.id ? (
  <>
    <Box sx={{ width: 48, height: 48, borderRadius: 2, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 1px #222', overflow: 'hidden', marginBottom: 1 }}>
      {order.image ? (
        <img src={order.image} alt={order.product} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, background: 'transparent', cursor: 'pointer' }} onClick={() => setEnlargedImage(order.image)} />
      ) : (
        <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 12 }}>
          غير متوفر
        </Box>
      )}
    </Box>
    <input
      accept="image/*"
      type="file"
      capture="environment"
      style={{ display: 'none' }}
      id={`image-upload-${order.id}`}
      onChange={async e => {
        const file = e.target.files[0];
        if (file) {
          setImageLoading(true);
          try {
            const compressedBase64 = await compressImage(file, 800, 0.7);
            handleFieldChange(order.id, 'image', compressedBase64);
          } catch (err) {
            alert('حدث خطأ أثناء ضغط الصورة');
          }
          setImageLoading(false);
        }
      }}
    />
    <label htmlFor={`image-upload-${order.id}`}>
      <Button variant="outlined" size="small" component="span" sx={{ mt: 1 }}>
        تغيير
      </Button>
    </label>
  </>

                        ) : (
                          <Box sx={{ width: 48, height: 48, borderRadius: 2, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 1px #222', overflow: 'hidden' }}>
                            {order.image ? (
                              <img src={order.image} alt={order.product} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, background: 'transparent', cursor: 'pointer' }} onClick={() => setEnlargedImage(order.image)} />
                            ) : (
                              <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 12 }}>
                                غير متوفر
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <TextField
                        type="number"
                        size="small"
                        value={order.order}
                        onChange={e => handleFieldChange(order.id, 'order', e.target.value)}
                        sx={{ width: 90 }}
                        inputProps={{ min: 0 }}
                      />
                    ) : order.order}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <TextField
                        type="number"
                        size="small"
                        value={order.finished}
                        onChange={e => handleFieldChange(order.id, 'finished', e.target.value)}
                        sx={{ width: 90 }}
                        inputProps={{ min: 0 }}
                      />
                    ) : order.finished}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <TextField
                        type="number"
                        size="small"
                        value={order.delivered}
                        onChange={e => handleFieldChange(order.id, 'delivered', e.target.value)}
                        sx={{ width: 90 }}
                        inputProps={{ min: 0 }}
                      />
                    ) : order.delivered}</TableCell>
                    <TableCell sx={{ color: GOLD, fontWeight: 700 }}>{order.order - order.finished}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={order.stage}
                          onChange={e => handleStageChange(order.id, e.target.value)}
                          displayEmpty
                          sx={{ fontWeight: 700, color: STAGE_META[order.stage]?.color || '#fff', background: 'rgba(20,20,20,0.88)' }}
                          renderValue={selected => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {STAGE_META[selected]?.icon}
                              <span>{selected || 'حدد المرحلة'}</span>
                            </Box>
                          )}
                        >
                          {Object.keys(STAGE_META).map(stage => (
  <MenuItem key={stage} value={stage} sx={{ color: STAGE_META[stage]?.color || '#fff' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {STAGE_META[stage]?.icon}
      <span>{stage}</span>
    </Box>
  </MenuItem>
))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: STAGE_META[order.stage]?.color || '#fff' }}>
                        {STAGE_META[order.stage]?.icon}
                        <span>{order.stage}</span>
                      </Box>
                    )}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={order.subStage}
                          onChange={e => handleSubStageChange(order.id, e.target.value)}
                          displayEmpty
                          sx={{ fontWeight: 700, color: POST_PRESS_META[order.subStage]?.color || '#fff', background: 'rgba(20,20,20,0.88)' }}
                          renderValue={selected => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {POST_PRESS_META[selected]?.icon}
                              <span>{selected || 'حدد المرحلة الفرعية'}</span>
                            </Box>
                          )}
                        >
                          {(order.stage === 'تجهيز بعد الطباعة' ? Object.keys(POST_PRESS_META) : ['غير متوفر']).map(sub => (
                            <MenuItem key={sub} value={sub} sx={{ color: POST_PRESS_META[sub]?.color || '#fff' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {POST_PRESS_META[sub]?.icon}
                                <span>{sub}</span>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      order.stage === 'Post-Press' && POST_PRESS_META[order.subStage] ?
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: POST_PRESS_META[order.subStage]?.color }}>
                          {POST_PRESS_META[order.subStage]?.icon}
                          <span>{order.subStage}</span>
                        </Box>
                      : <span>{order.subStage}</span>
                    )}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <TextField
                        type="date"
                        size="small"
                        value={order.expectedFinish || ''}
                        onChange={e => handleFieldChange(order.id, 'expectedFinish', e.target.value)}
                        sx={{ width: 150 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (order.expectedFinish ? (() => { const d = new Date(order.expectedFinish); return !isNaN(d) ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth()+1).padStart(2, '0')}/${d.getFullYear()}` : ''; })() : '')}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <TextField
                        type="date"
                        size="small"
                        value={order.orderExpectedFinish || ''}
                        onChange={e => handleFieldChange(order.id, 'orderExpectedFinish', e.target.value)}
                        sx={{ width: 150 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (order.orderExpectedFinish ? new Date(order.orderExpectedFinish).toLocaleDateString() : '')}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{editingRowId === order.id ? (
                      <TextField
                        size="small"
                        value={order.notes}
                        onChange={e => handleFieldChange(order.id, 'notes', e.target.value)}
                        sx={{ width: 180 }}
                        multiline
                        maxRows={2}
                      />
                    ) : (
                      order.notes && order.notes.length > 0 ? order.notes : ''
                    )}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      <Button variant="outlined" size="small" onClick={() => handleViewHistory(order)} sx={{ color: GOLD, borderColor: GOLD, fontWeight: 700 }}>
                        عرض
                      </Button>
                    </TableCell>
                    {role === 'admin' || role != 'viewer' ?<TableCell sx={{ color: '#fff' }}>
                      {editingRowId === order.id ? (
                        <>
                          <Button variant="contained" color="success" size="small" sx={{ mr: 1 }} onClick={() => handleSaveRow(order.id)}>
                            حفظ
                          </Button>
                          <Button variant="outlined" color="error" size="small" onClick={handleCancelEditRow}>
                            إلغاء
                          </Button>
                        </>
                      ) : (
                        <Button variant="contained" size="small" sx={{ background: GOLD, color: '#222', fontWeight: 700 }} onClick={() => handleEditRow(order.id)}>
                          تعديل
                        </Button>
                      )}
                    </TableCell> : null}
                    {user && role === 'admin' && (
                      <TableCell align="center">
                        <Tooltip title="حذف">
                          <IconButton color="error" onClick={() => setDeleteDialog({ open: true, order })}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="عدد الصفوف في الصفحة"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} من ${count !== -1 ? count : `أكثر من ${to}`}`}
          />
        </Paper>
      </Box>
    );
  }

  
